<template>
    <div id="tasks">
        <AppBreadCrumb :select="true" :show-subjects="false" @type-workflow-list="workflowList">
            <div class="custom-select" v-if="localType === 'kanban'" v-click-outside="closeDropdown">
                <div
                    class="selected-option"
                    @click="toggleDropdown"
                    v-tippy="{
                        content: selectedWorkflowTitle,
                        placement: 'right',
                        followCursor: true,
                    }"
                >
                    {{ selectedWorkflowTitle }}
                </div>
                <div v-if="dropdownOpen" class="options-workflow">
                    <div
                        v-for="option in getWorkflows()"
                        :key="option.value"
                        class="option-workflow"
                        @click="selectOption(option)"
                        v-tippy="{
                            content: option.label,
                            placement: 'right',
                            followCursor: true,
                        }"
                    >
                        {{
                            option.label.length > 15
                                ? option.label.substring(0, 15) + $t("task.workflowOptionEllipsis")
                                : option.label
                        }}
                    </div>
                </div>
            </div>
        </AppBreadCrumb>

        <transition name="fade">
            <div v-if="updatedCount > 0" class="task-update-notification">
                <div class="notification-close" @click="ignoreUpdates">
                    <span class="material-icons">close</span>
                </div>
                <div class="notification-content">
                    <div class="notification-icon">
                        <span class="material-icons">sync</span>
                    </div>
                    <div class="notification-message">
                        <div class="notification-title">{{ $t("task.workspaceChangesTitle") }}</div>
                        <div class="notification-description">
                            {{ $t("task.workspaceChangesDescription", { count: updatedCount }) }}
                        </div>
                        <div class="notification-actions">
                            <a-button type="primary" @click="refreshTasks">
                                <span class="material-icons">refresh</span> {{ $t("message.refresh") }}
                            </a-button>
                            <a-button @click="ignoreUpdates">{{ $t("task.ignoreWorkspaceUpdates") }}</a-button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="route-page-content breadcrumb-content">
            <SidebarChild
                :items="filteredMenuItems"
                :selected-item="localType"
                :focusActive="true"
                @selected="handleTypeChange"
                @clicked="fnClicked"
            />
            <div class="page-content-wrapper">
                <div v-if="['kanban', 'list', 'archive', 'done'].includes(localType)" class="head-bar">
                    <task-filter-bar
                        v-if="localType === 'kanban'"
                        ref="taskFilterBar"
                        :show-overdue="Boolean(showOverdue)"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :createdBy="createdBy"
                        :workflow-id="workflowId || null"
                        :checked-items="checkedItems"
                        :checked-all-tasks="checkedAllTask"
                        :checked-any-tasks="checkedAnyTasks()"
                        :batch-operation-items="batchOperationItems()"
                        :show-operations="true"
                        :show-batch-operations="!!batchOperationItems().length"
                        :can-close-tasks="canCloseSelectedTasks"
                        :selectedPriorities="selectedPriorities"
                        :selectedTags="selectedTags"
                        :searchQuery="searchQuery"
                        :dateRange="dateRange"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :updateAtRange="updateAtRange"
                        :sortBy="sortBy"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:createdBy="createdBy = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:searchQuery="searchQuery = $event"
                        @apply-filters="handleApplyFilters"
                        @check-all-tasks="changeCheckedTasks"
                        @change-stage="checkDraggingTaskPermission"
                        @delete-tasks="checkDeleteTaskPermission"
                        @close-tasks="openCloseTasksModal"
                        @search="handleSearchQuery"
                        @toggle-reset-filters="handleResetFilters"
                    >
                        <template #right-buttons>
                            <div class="task-actions-container">
                                <div class="task-action-button" @click="beforeCreateTaskRow(createTask)">
                                    <span class="md-icon action-icon create-icon">add_task</span>
                                    <span class="action-text">{{ $t("task.createTask") }}</span>
                                </div>
                                <div class="action-divider"></div>
                                <div class="task-action-button" @click="openArchive">
                                    <span class="md-icon action-icon archive-icon">archive</span>
                                    <span class="action-text">{{ $t("message.archive") }}</span>
                                </div>
                                <div class="action-divider"></div>
                                <div class="task-action-button" @click="openCloseTask">
                                    <span class="md-icon action-icon done-icon">flaky</span>
                                    <span class="action-text">{{ $t("task.closedTasks") }}</span>
                                </div>
                            </div>
                        </template>
                    </task-filter-bar>

                    <task-filter-bar
                        v-if="localType === 'list' || localType === 'archive' || localType === 'done'"
                        ref="taskFilterBar"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :createdBy="createdBy"
                        :workflow-id="workflowId || null"
                        :show-overdue="Boolean(showOverdue)"
                        :selectedPriorities="selectedPriorities"
                        :selectedTags="selectedTags"
                        :searchQuery="searchQuery"
                        :dateRange="dateRange"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :updateAtRange="updateAtRange"
                        :sortBy="sortBy"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:searchQuery="searchQuery = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:createdBy="createdBy = $event"
                        @apply-filters="handleApplyFilters"
                        @toggle-reset-filters="handleResetFilters"
                        @search="handleSearchQuery"
                        @filter-assignees="handleFilterAssignees"
                        @filter-other="handleFilterOther"
                    />
                    <div
                        v-if="localType === 'done' || localType === 'archive' || localType === 'list'"
                        class="workflow-select-container"
                    >
                        <a-select
                            :value="workflowId || ''"
                            :placeholder="$t('task.selectWorkflow')"
                            style="width: 250px"
                            :disabled="false"
                            @change="handleWorkflowChange"
                            allowClear
                        >
                            <a-select-option value="">{{ $t("message.all") }}</a-select-option>
                            <a-select-option v-for="option in getWorkflows()" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </a-select-option>
                        </a-select>
                    </div>
                </div>
                <div class="board">
                    <Workflow v-if="localType === 'workflowList'" @selected-workflow="changeWorkflow" />
                    <TaskDraggable
                        v-if="localType === 'kanban' && workflowId"
                        ref="taskDraggable"
                        :workflow-id="workflowId || null"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :createdBy="createdBy"
                        :showOverdue="Boolean(showOverdue)"
                        :search="searchQuery"
                        :selectedPriorities="selectedPriorities"
                        :selectedPipelines="selectedPipelines"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :dateRange="dateRange"
                        :selectedTags="selectedTags"
                        :updateAtRange="updateAtRange"
                        :sortBy="sortBy"
                        :tasks="tasks"
                        :basicTaskForm="basicTaskForm"
                        :checked-items="checkedItems"
                        :isLoading="isLoading"
                        :retryCount="retryCount"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:selectedPipelines="selectedPipelines = $event"
                        @update:search="searchQuery = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:createdBy="createdBy = $event"
                        @updateCheckedItems="checkedItems = $event"
                        @updateBasicTaskForm="basicTaskFormUpdated"
                        @updateTaskList="taskListUpdated"
                        @apply-filters="handleApplyFilters"
                    />
                    <KanbanList
                        v-if="localType === 'list'"
                        ref="kanbanList"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :workflow-id="workflowId || null"
                        :showOverdue="Boolean(showOverdue)"
                        :search="searchQuery"
                        :selectedPriorities="selectedPriorities"
                        :selectedPipelines="selectedPipelines"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :updateAtRange="updateAtRange"
                        :dateRange="dateRange"
                        :createdBy="createdBy"
                        :selectedTags="selectedTags"
                        :sortBy="sortBy"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:selectedPipelines="selectedPipelines = $event"
                        @update:search="searchQuery = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:createdBy="createdBy = $event"
                        @apply-filters="handleApplyFilters"
                    />
                    <Tags v-if="localType === 'tags'" />
                    <TaskArchiveList
                        v-if="localType === 'archive'"
                        ref="taskArchiveList"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :workflow-id="workflowId || null"
                        :showOverdue="Boolean(showOverdue)"
                        :search="searchQuery"
                        :selectedPriorities="selectedPriorities"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :updateAtRange="updateAtRange"
                        :dateRange="dateRange"
                        :createdBy="createdBy"
                        :selectedTags="selectedTags"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:search="searchQuery = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:createdBy="createdBy = $event"
                        @apply-filters="handleApplyFilters"
                    />
                    <DoneTask
                        v-if="localType === 'done'"
                        ref="doneTask"
                        :assignees="assignees"
                        :owners="owners"
                        :followers="followers"
                        :workflow-id="workflowId || null"
                        :showOverdue="Boolean(showOverdue)"
                        :search="searchQuery"
                        :selectedPriorities="selectedPriorities"
                        :startDateRange="startDateRange"
                        :endDateRange="endDateRange"
                        :updateAtRange="updateAtRange"
                        :dateRange="dateRange"
                        :createdBy="createdBy"
                        :selectedTags="selectedTags"
                        @update:assignees="assignees = $event"
                        @update:owners="owners = $event"
                        @update:followers="followers = $event"
                        @update:showOverdue="showOverdue = Boolean($event)"
                        @update:selectedPriorities="selectedPriorities = $event"
                        @update:selectedTags="selectedTags = $event"
                        @update:search="searchQuery = $event"
                        @update:dateRange="dateRange = $event"
                        @update:startDateRange="startDateRange = $event"
                        @update:endDateRange="endDateRange = $event"
                        @update:updateAtRange="updateAtRange = $event"
                        @update:createdBy="createdBy = $event"
                        @apply-filters="handleApplyFilters"
                    />
                    <TaskNotification v-if="localType === 'taskNotification'" />
                    <AutomationList v-if="localType === 'automation'" type="event" module="task" />
                    <TaskReports v-if="localType === 'reports'" :workflow-id="workflowId" />
                    <WorkFlowSandbox v-if="localType === 'sandbox'" />
                </div>
            </div>
        </div>
        <transition name="modal-fade">
            <div
                v-if="deleteMultipleTasksModal"
                class="custom-modal-overlay"
                @click.self="deleteMultipleTasksModal = false"
            >
                <div class="delete-modal-container">
                    <div class="delete-modal-header">
                        <div class="warning-icon-wrapper">
                            <svg
                                class="warning-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <div class="delete-modal-content">
                            <h3 class="delete-modal-title">{{ $t("task.deleteTask") }}</h3>
                            <p class="delete-modal-message">{{ $t("task.deleteTaskCont") }}</p>
                            <div class="delete-modal-info">
                                <svg
                                    class="info-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <span>{{ $t("task.tasksWillBeDeletedCount", { count: batchOperationItems().length }) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="delete-modal-footer">
                        <button class="modal-btn modal-btn-cancel" @click="deleteMultipleTasksModal = false">
                            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M6 18L18 6M6 6l12 12"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            {{ $t("chatbotv2.dismiss") }}
                        </button>
                        <button class="modal-btn modal-btn-danger" @click="deleteMultipleTasks">
                            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            {{ $t("chatbotv2.delete") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="modal-fade">
            <div v-if="closeTasksModal" class="custom-modal-overlay" @click.self="closeTasksModal = false">
                <div class="close-tasks-modal-container">
                    <div class="close-tasks-modal-header">
                        <div class="success-icon-wrapper">
                            <svg
                                class="success-icon"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                        <div class="close-tasks-modal-content">
                            <h3 class="close-tasks-modal-title">{{ $t("task.closeTasksBulkTitle") }}</h3>
                            <p class="close-tasks-modal-message">
                                {{ $t("task.closeTasksBulkMessage") }}
                            </p>
                            <div class="close-tasks-modal-info">
                                <svg
                                    class="info-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                                <span>{{ $t("task.tasksWillBeClosedCount", { count: batchOperationItems().length }) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="close-tasks-modal-footer">
                        <button class="modal-btn modal-btn-cancel" @click="closeTasksModal = false">
                            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M6 18L18 6M6 6l12 12"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            {{ $t("message.cancel") }}
                        </button>
                        <button class="modal-btn modal-btn-success" @click="confirmCloseTasks">
                            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            {{ $t("task.batchConfirm") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <transition name="modal-fade">
            <div v-if="changeStageModal" class="custom-modal-overlay" @click.self="changeStageModal = false">
                <div class="custom-modal-container">
                    <div class="custom-modal-header">
                        <div class="modal-header-content">
                            <div class="modal-icon-wrapper">
                                <svg
                                    class="modal-icon"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                                        stroke="currentColor"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                            <div class="modal-title-section">
                                <h3 class="modal-title">{{ $t("message.changeStage") }}</h3>
                                <p class="modal-subtitle">{{ $t("message.selectedCandidates") }}</p>
                            </div>
                        </div>
                        <button class="modal-close-btn" @click="changeStageModal = false">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M18 6L6 18M6 6l12 12"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </button>
                    </div>

                    <div class="custom-modal-body">
                        <div class="pipeline-selection-grid">
                            <div
                                v-for="pipeline in workflows[workflowId].pipeline"
                                :key="pipeline._id"
                                :class="['pipeline-card', { selected: selectedPipelineId === pipeline._id }]"
                                @click="selectedPipelineId = pipeline._id"
                            >
                                <div class="pipeline-card-inner">
                                    <div
                                        class="pipeline-color-indicator"
                                        :style="{ backgroundColor: pipeline.color || '#47499d' }"
                                    ></div>
                                    <div class="pipeline-content">
                                        <div class="pipeline-name">{{ pipeline.name }}</div>
                                        <div class="pipeline-details">
                                            <span class="pipeline-order">{{
                                                $t("task.pipelineOrderLabel", { order: pipeline.order })
                                            }}</span>
                                            <span class="pipeline-count">{{
                                                $t("task.pipelineTaskCountLabel", { count: pipeline.taskCount || 0 })
                                            }}</span>
                                        </div>
                                    </div>
                                    <div class="pipeline-check">
                                        <svg
                                            v-if="selectedPipelineId === pipeline._id"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M20 6L9 17l-5-5"
                                                stroke="currentColor"
                                                stroke-width="2.5"
                                                stroke-linecap="round"
                                                stroke-linejoin="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="custom-modal-footer">
                        <button class="modal-btn modal-btn-cancel" @click="changeStageModal = false">{{
                            $t("message.cancel")
                        }}</button>
                        <button
                            class="modal-btn modal-btn-primary"
                            @click="changeTaskPipeline"
                            :disabled="!selectedPipelineId"
                        >
                            <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M5 13l4 4L19 7"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                            {{ $t("message.save") }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <TaskArchiveModal
            v-if="showArchiveModal"
            :workflow="workflowId"
            @close="showArchiveModal = false"
        />
        <TaskDoneModal
            v-if="showDoneModal"
            :workflow="workflowId"
            @close="showDoneModal = false"
        />
    </div>
</template>
<script>
import AppBreadCrumb from "../../../components/common/AppBreadCrumb";
import { mapActions, mapGetters, mapState } from "vuex";
import KanbanList from "../../../components/task/TaskListView.vue";
import TaskDraggable from "./components/TaskDraggable.vue";
import SupsisButton from "../../../components/common/SupsisButtonOld.vue";
import SidebarChild from "../../../components/Sidebar/SidebarChild.vue";
import Subjects from "../Settings/pages/company/Departments.vue";
import BannedVisitors from "../Settings/pages/chat/BannedVisitors.vue";
import Users from "../Settings/pages/company/Users.vue";
import DeleteModal from "./components/DeleteModal.vue";
import Avatar from "vue-avatar";
import Workflow from "../../../components/task/Workflow.vue";
import Tags from "../../../components/task/Tags.vue";
import TaskArchiveList from "../../../components/task/TaskArchiveList.vue";
import AutomationList from "../Automation/pages/automations/AutomationList.vue";
import DoneTask from "../../../components/task/DoneTask.vue";
import { systemMessageStream, SystemMessageTypes } from "../../../utils/systemPubSub";
import { sendSocketMessage, socketMessageStream } from "../../../utils/socketStream";
import TaskFilterBar from "../../../components/task/TaskFilterBar.vue";
import TaskNotification from "../../../components/task/TaskNotification.vue";
import TaskReports from "../../../components/task/TaskReports.vue";
import WorkFlowSandbox from "./components/WorkFlowSandbox.vue";
import TaskArchiveModal from "../../../components/task/Settings/TaskArchiveModal.vue";
import TaskDoneModal from "../../../components/task/Settings/TaskDoneModal.vue";
import { PERMISSION_TYPES } from "../../../language/permissionTypes";

const COMMANDS = {
    TASK_UPDATED: "task updated",
};

export default {
    name: "TasksHome",
    components: {
        Users,
        BannedVisitors,
        Subjects,
        SidebarChild,
        AppBreadCrumb,
        KanbanList,
        TaskDraggable,
        SupsisButton,
        DeleteModal,
        Avatar,
        Workflow,
        Tags,
        TaskArchiveList,
        AutomationList,
        DoneTask,
        TaskFilterBar,
        TaskNotification,
        TaskReports,
        WorkFlowSandbox,
        TaskArchiveModal,
        TaskDoneModal,
    },
    data() {
        return {
            searchQuery: "",
            taskId: this.$route.query.taskId || null,
            workflowId: this.$route.query.workflow || null,
            localType: this.$route.query.type || "workflowList",
            deleteWorkflowModal: false,
            showOverdue: false,
            dateRange: null,
            createdBy: [],
            showArchiveModal: false,
            showDoneModal: false,
            options: [
                {
                    value: "kanban",
                    label: this.$t("task.kanbanBoard"),
                },
                {
                    value: "list",
                    label: this.$t("task.listView"),
                },
                {
                    value: "tags",
                    label: this.$t("message.tags"),
                },
            ],
            assignees: [],
            checkedAllTask: false,
            deleteMultipleTasksModal: false,
            changeStageModal: false,
            closeTasksModal: false,
            selectedPipelineId: null,
            menuItems: [
                {
                    enabled: true,
                    key: "workflowList",
                    label: this.$t("task.workflow"),
                    icon: {
                        type: "material-icons",
                        name: "rebase_edit",
                    },
                },
                {
                    enabled: true,
                    key: "list",
                    label: this.$t("task.listView"),
                    icon: {
                        type: "material-icons",
                        name: "list",
                    },
                },
                {
                    enabled: true,
                    key: "tags",
                    label: this.$t("message.tags"),
                    icon: {
                        type: "material-icons",
                        name: "label",
                    },
                },
                {
                    enabled: true,
                    key: "archive",
                    label: this.$t("message.archive"),
                    icon: {
                        type: "material-icons",
                        name: "archive",
                    },
                },
                {
                    enabled: true,
                    key: "done",
                    label: this.$t("message.completedTasks"),
                    icon: {
                        type: "material-icons",
                        name: "edit_attributes",
                    },
                },
                {
                    enabled: true,
                    key: "taskNotification",
                    label: this.$t("task.taskNotifications"),
                    icon: {
                        type: "material-icons",
                        name: "notifications",
                    },
                },
                {
                    enabled: true,
                    key: "automation",
                    label: this.$t("automation.automations"),
                    icon: {
                        type: "component",
                        name: "AutomationIcon",
                        class: "myIcon",
                        style: { fill: "var(--color-icon-primary-default)" },
                    },
                },
                {
                    enabled: true,
                    key: "reports",
                    label: this.$t("task.reports"),
                    icon: {
                        type: "material-icons",
                        name: "bar_chart",
                    },
                },
                {
                    enabled: true,
                    key: "workflowSettings",
                    label: this.$t("task.workflowSettings"),
                    clickEvent: true,
                    icon: {
                        type: "material-icons",
                        name: "tune",
                    },
                },
                {
                    enabled: true,
                    key: "settings",
                    label: this.$t("message.settings"),
                    clickEvent: true,
                    icon: {
                        type: "material-icons",
                        name: "settings",
                    },
                },
                {
                    enabled: true,
                    key: "sandbox",
                    label: this.$t("task.trashBin"),
                    icon: {
                        type: "material-icons",
                        name: "delete",
                    },
                },
            ],
            dropdownOpen: false,
            selectedOption: { label: this.$t("task.selectOption"), value: null },
            checkedItems: {},
            tasks: {},
            basicTaskForm: {},
            isLoading: false,
            retryCount: 0,
            maxRetries: 3,
            cancels: [],
            updatedCount: 0,
            selectedPriorities: [],
            owners: [],
            followers: [],
            selectedTags: [],
            startDateRange: null,
            endDateRange: null,
            updateAtRange: null,
            selectedPipelines: [],
            sortBy: null,
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            usersAvatar: (state) => state.user.users.data,
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
            isRoot: (state) => state.auth.user.role.isRoot,
            route: (state) => state.app.route,
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        filteredUsersAvatar() {
            const workflowKeys = Object.keys(this.workflows);
            const workflows = workflowKeys.map((key) => this.workflows[key]);
            const selectedWorkflows = workflows.filter((workflow) => workflow._id === this.workflowId);
            if (selectedWorkflows.length === 0 || selectedWorkflows[0].visibility.users.length === 0) {
                return this.usersAvatar;
            }
            const usersFromSelectedWorkflows = selectedWorkflows.map((workflow) => workflow.visibility.users);
            const users = usersFromSelectedWorkflows.flat();
            const usersAvatar = users.map((userId) => this.users[userId]);
            return usersAvatar;
        },
        selectedWorkflowTitle() {
            if (!this.workflowId) return this.$t("task.selectOption");

            const workflow = this.workflows[this.workflowId];
            if (!workflow) return this.$t("task.selectOption");

            const title = workflow.title;
            return title.length > 15 ? title.substring(0, 15) + "..." : title;
        },
        selectedWorkflow() {
            return this.workflows[this.workflow];
        },
        canCloseSelectedTasks() {
            if (!this.workflowId || !this.workflows[this.workflowId]) {
                return false;
            }

            const workflow = this.workflows[this.workflowId];
            const completedPipeline = workflow.pipeline.find((p) => p.type === "completed");

            if (!completedPipeline) {
                return false;
            }

            const selectedTaskIds = this.batchOperationItems();
            if (selectedTaskIds.length === 0) {
                return false;
            }

            const allTasks = Object.values(this.tasks).flat();
            const selectedTasks = allTasks.filter((task) => selectedTaskIds.includes(task._id));

            return selectedTasks.every((task) => task.pipelineStage === completedPipeline._id);
        },
        filteredMenuItems() {
            return this.menuItems.filter((item) => {
                if (item.key === "workflowSettings") {
                    return this.localType === "kanban" && this.workflowId && this.taskDetailPermission();
                }
                return item.enabled;
            });
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            deleteTask: "tasks/deleteTask",
            multipleTaskDelete: "tasks/multipleTaskDelete",
            multipleTaskMove: "tasks/multipleTaskMove",
            multipleTaskDone: "tasks/multipleTaskDone",
            deleteWorkflow: "tasks/deleteWorkflow",
            getWorkflowTasks: "tasks/getWorkflowTasks",
            setWorkflows: "tasks/setWorkflows",
            incWorkflowPipelineTaskCount: "tasks/incWorkflowPipelineTaskCount",
            decWorkflowPipelineTaskCount: "tasks/decWorkflowPipelineTaskCount",
            getWorkFlows: "tasks/getWorkFlows",
        }),
        refreshTasks() {
            this.updatedCount = 0;
            this.$refs.taskDraggable.fetchPipelines().then(() => {
                this.initWorkflowTasks();
            });
        },
        ignoreUpdates() {
            this.updatedCount = 0;
        },
        publishTaskUpdate(workflowId) {
            sendSocketMessage(COMMANDS.TASK_UPDATED, {
                siteId: this.$store.state.site.site._id,
                userId: this.$store.state.auth.user._id,
                workflowId: workflowId || this.workflowId,
            });
        },
        basicTaskFormUpdated(basicTaskForm) {
            this.basicTaskForm = basicTaskForm;
        },
        taskListUpdated(tasks) {
            this.tasks = tasks;
        },
        checkDraggingTaskPermission() {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[2].includes(this.user._id)) {
                this.changeStageModal = true;
            } else {
                this.$notification.error({
                    message: this.$t("task.permissionDeniedMoveTask"),
                    description: this.$t("message.noPermissionDescription"),
                });
            }
        },
        checkDeleteTaskPermission() {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[3].includes(this.user._id)) {
                this.deleteMultipleTasksModal = true;
            } else {
                this.$notification.error({
                    message: this.$t("task.permissionDeniedDeleteTask"),
                    description: this.$t("message.noPermissionDescription"),
                });
            }
        },
        beforeCreateTaskRow(fn) {
            const workflowId = this.workflowId;
            const workflow = this.workflows[workflowId];
            if (workflow) {
                const todoPipeline = workflow.pipeline.sort((a, b) => a.order - b.order).find((x) => x.type === "todo");
                this.$feature.beforeCreateTaskRow(fn, todoPipeline.taskCount);
            } else {
                console.error("Workflow not found");
            }
        },
        changeWorkflow(id) {
            // Önceki workflow'un tasks'larını temizle
            this.tasks = {};
            this.checkedItems = {};

            // Yeni workflow'u set et
            this.localType = "kanban";
            this.workflowId = id;

            this.updateRouteParams(true);

            // Filtreleri sıfırla ve yeni workflow için tasks'ları yükle
            this.$nextTick(() => {
                this.resetFilters();
                if (this.workflowId) {
                    this.initWorkflowTasks();
                }
            });
        },
        workflowList() {
            this.localType = "workflowList";
            this.updateRouteParams(true);
        },
        getWorkflows() {
            return Object.keys(this.workflows)
                .map((workflow) => this.workflows[workflow])
                .filter((workflow) => {
                    if (this.isRoot) {
                        return true;
                    }
                    const visibility = workflow.visibility || {};
                    const users = visibility.users || [];
                    const departments = visibility.departments || [];
                    const userSubjects = this.user.subjects || [];
                    const userId = this.user._id || null;
                    if (users.includes(userId) && departments.length === 0) {
                        return true;
                    } else if (departments.some((dept) => userSubjects.includes(dept)) && users.length === 0) {
                        return true;
                    } else if (users.includes(userId) || departments.some((dept) => userSubjects.includes(dept))) {
                        return true;
                    } else if (users.length === 0 && departments.length === 0) {
                        return true;
                    }
                    return false;
                })
                .map((workflow) => ({
                    value: workflow._id,
                    label: workflow.title,
                }));
        },
        createWorkflow() {
            this.addNewWindow({
                type: "create-workflow",
            });
        },
        createTask() {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(
                (permission) => workflow.permissions[permission]
            );
            const perm = permissions.every((permission) => permission.length === 0);
            if (perm || permissions[0].includes(this.user._id)) {
                this.addNewWindow({
                    title: this.$t("task.createTask"),
                    type: "create-task",
                    payload: {
                        workflow: workflow._id,
                    },
                });
            } else {
                this.$notification.error({
                    message: this.$t("task.permissionDeniedCreateTask"),
                    description: this.$t("message.noPermissionDescription"),
                });
            }
        },
        openArchive() {
            this.showArchiveModal = true;
        },
        openCloseTask() {
            this.showDoneModal = true;
        },
        openTaskSettings() {
            this.addNewWindow({
                type: "task-settings",
                footer: false,
            });
        },
        batchOperationItems() {
            return Object.keys(this.checkedItems).filter((key) => this.checkedItems[key]);
        },
        checkedAnyTasks() {
            const tasks = Object.values(this.tasks).flat();
            return !this.checkedAllTask && tasks.some((task) => this.checkedItems[task._id]);
        },
        checkedAllTasks() {
            const tasks = Object.values(this.tasks).flat();
            return tasks.every((task) => this.checkedItems[task._id]);
        },
        changeCheckedTasks(e) {
            const val = e.target.checked;
            if (!val) {
                this.checkedItems = {};
                return;
            }
            this.checkedItems = Object.values(this.tasks)
                .flat()
                .reduce((acc, task) => {
                    acc[task._id] = true;
                    return acc;
                }, {});
        },
        deleteMultipleTasks() {
            const checkedTaskIds = this.batchOperationItems();
            const flattenTasks = Object.values(this.tasks).flat();
            const tasks = flattenTasks.filter((task) => checkedTaskIds.includes(task._id));
            const affectedPipelineIds = [];
            const seenPipeline = {};
            for (let di = 0; di < tasks.length; di++) {
                const stageId = tasks[di].pipelineStage;
                if (stageId && !seenPipeline[stageId]) {
                    seenPipeline[stageId] = true;
                    affectedPipelineIds.push(stageId);
                }
            }

            this.multipleTaskDelete({ taskIds: checkedTaskIds, tasks }).then(() => {
                this.publishTaskUpdate();
                const self = this;
                const pipelineIds = affectedPipelineIds.slice();
                return new Promise(function (resolve) {
                    self.$nextTick(function () {
                        const ref = self.$refs.taskDraggable;
                        if (ref && typeof ref.refillPipelineTasks === "function") {
                            ref.refillPipelineTasks(pipelineIds).then(resolve).catch(resolve);
                        } else {
                            resolve();
                        }
                    });
                });
            });

            this.$notification.success({
                message: this.$t("task.tasksBulkDeletedTitle"),
                description: this.$t("task.tasksBulkDeletedDescription", { count: checkedTaskIds.length }),
            });
            this.tasks = Object.keys(this.tasks).reduce((acc, key) => {
                acc[key] = this.tasks[key].filter((task) => !checkedTaskIds.includes(task._id));
                return acc;
            }, {});
            this.checkedItems = {};
            this.deleteMultipleTasksModal = false;
        },
        changeTaskPipeline() {
            const taskIds = this.batchOperationItems();
            const taskMap = Object.values(this.tasks)
                .flat()
                .reduce((acc, task) => {
                    acc[task._id] = task;
                    return acc;
                }, {});
            const tasks = taskIds.map((taskId) => taskMap[taskId]).filter(Boolean);
            const destinationPipelineId = this.selectedPipelineId;
            const affectedPipelineIds = [];
            const seenPipeline = {};
            for (let mi = 0; mi < tasks.length; mi++) {
                const stageId = tasks[mi].pipelineStage;
                if (stageId && !seenPipeline[stageId]) {
                    seenPipeline[stageId] = true;
                    affectedPipelineIds.push(stageId);
                }
            }
            if (destinationPipelineId && !seenPipeline[destinationPipelineId]) {
                affectedPipelineIds.push(destinationPipelineId);
            }

            this.multipleTaskMove({
                payload: {
                    taskIds,
                    pipelineId: this.selectedPipelineId,
                },
                tasks,
            }).then(() => {
                this.publishTaskUpdate();
                const self = this;
                const pipelineIds = affectedPipelineIds.slice();
                return new Promise(function (resolve) {
                    self.$nextTick(function () {
                        const ref = self.$refs.taskDraggable;
                        if (ref && typeof ref.refillPipelineTasks === "function") {
                            ref.refillPipelineTasks(pipelineIds).then(resolve).catch(resolve);
                        } else {
                            resolve();
                        }
                    });
                });
            });

            this.tasks = {
                ...Object.keys(this.tasks).reduce((acc, key) => {
                    acc[key] = this.tasks[key].filter((task) => !taskIds.includes(task._id));
                    return acc;
                }, {}),
                [this.selectedPipelineId]: [
                    ...tasks,
                    ...(this.tasks[this.selectedPipelineId] || []).filter((task) => !taskIds.includes(task._id)),
                ],
            };
            this.$notification.success({
                message: this.$t("task.tasksBulkMovedTitle"),
                description: this.$t("task.tasksBulkMovedDescription"),
            });
            this.checkedItems = {};
            this.selectedPipelineId = null;
            this.changeStageModal = false;
        },
        toggleSearchTask(searchQuery) {
            this.searchQuery = searchQuery;
            this.initWorkflowTasks();
        },
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
        },
        selectOption(option) {
            this.selectedOption = option;
            this.workflowId = option.value;
            this.dropdownOpen = false;
            if (this.localType === "kanban") {
                this.updateRouteParams(true);
            }
        },
        openTask(taskId) {
            if (this.localType === "kanban" && this.taskId !== taskId) {
                this.taskId = taskId;
                this.updateRouteParams(true);
            }
            this.addNewWindow({
                type: "editor-task",
                title: this.$t("task.taskDetailsWindowTitle"),
                payload: {
                    taskId: taskId,
                },
            });
        },
        closeDropdown() {
            this.dropdownOpen = false;
        },
        updateRouteParams(usePush = false) {
            const type = this.localType;
            const query = { type };
            if (type === "kanban") {
                query.workflow = this.workflowId;
                const existingTaskId = this.taskId || (this.$route && this.$route.query && this.$route.query.taskId);
                if (existingTaskId) {
                    query.taskId = existingTaskId;
                }
            }
            const currentQuery = this.$route.query || {};
            const isQuerySame = JSON.stringify(currentQuery) === JSON.stringify(query);
            if (!isQuerySame) {
                if (usePush) {
                    this.$router.push({
                        path: this.$route.path,
                        query: query,
                    });
                } else {
                    this.$router.replace({
                        path: this.$route.path,
                        query: query,
                    });
                }
            }
            this.$emit("update:type", this.localType);
        },
        handleTypeChange(selectedItem) {
            this.localType = selectedItem.key;
            this.resetFilters();
            this.updateRouteParams(true);
            this.$emit("update:type", this.localType);
        },
        handlePriorityChange(priorities) {
            this.selectedPriorities = [...priorities];

            if (this.localType === "list") {
                this.$nextTick(() => {
                    if (this.$refs.kanbanList) {
                        this.$refs.kanbanList.getTasks();
                    }
                });
            } else if (this.localType === "kanban") {
                this.initWorkflowTasks();
            }
        },
        initTaskActions() {
            const taskActionSubscription = systemMessageStream.subscribe((message) => {
                const type = message.type;
                const data = message.payload;

                switch (type) {
                    case SystemMessageTypes.TASK_CREATED:
                        this.tasks = {
                            ...this.tasks,
                            [data.createdTask.pipelineStage]: [
                                data.createdTask,
                                ...(this.tasks[data.createdTask.pipelineStage] || []).sort((a, b) => a.order - b.order),
                            ],
                        };

                        this.incWorkflowPipelineTaskCount({
                            workflowId: data.createdTask.workflowId,
                            pipelineId: data.createdTask.pipelineStage,
                        });

                        this.publishTaskUpdate(data.createdTask.workflowId);
                        break;

                    case SystemMessageTypes.TASK_UPDATED:
                        const oldTask = Object.values(this.tasks)
                            .flat()
                            .find((task) => task._id === data.updatedTask._id);
                        if (oldTask) {
                            const oldPipelineId = oldTask.pipelineStage;
                            const newPipelineId = data.updatedTask.pipelineStage;
                            const isTaskMoved = oldPipelineId !== newPipelineId;

                            if (isTaskMoved) {
                                this.tasks = {
                                    ...this.tasks,
                                    [oldPipelineId]: this.tasks[oldPipelineId].filter(
                                        (task) => task._id !== data.updatedTask._id
                                    ),
                                    [newPipelineId]: [data.updatedTask, ...(this.tasks[newPipelineId] || [])].sort(
                                        (a, b) => a.order - b.order
                                    ),
                                };

                                this.decWorkflowPipelineTaskCount({
                                    workflowId: data.updatedTask.workflowId || this.workflowId,
                                    pipelineId: oldPipelineId,
                                });

                                this.incWorkflowPipelineTaskCount({
                                    workflowId: data.updatedTask.workflowId || this.workflowId,
                                    pipelineId: newPipelineId,
                                });
                            } else {
                                this.tasks = {
                                    ...this.tasks,
                                    [newPipelineId]: this.tasks[newPipelineId].map((task) =>
                                        task._id === data.updatedTask._id ? data.updatedTask : task
                                    ),
                                };
                            }
                        }

                        this.publishTaskUpdate(data.updatedTask.workflowId || this.workflowId);
                        break;

                    case SystemMessageTypes.TASK_DELETED:
                        const deletedTask = Object.values(this.tasks)
                            .flat()
                            .find((task) => task._id === data.taskId);
                        if (deletedTask) {
                            this.tasks = Object.keys(this.tasks).reduce((acc, key) => {
                                acc[key] = this.tasks[key].filter((task) => task._id !== deletedTask._id);
                                return acc;
                            }, {});

                            this.decWorkflowPipelineTaskCount({
                                workflowId: deletedTask.workflowId,
                                pipelineId: deletedTask.pipelineStage,
                            });

                            this.publishTaskUpdate(deletedTask.workflowId);
                        }
                        break;

                    case SystemMessageTypes.TASK_MOVED_BOARD:
                        const task = data.task;
                        this.tasks = {
                            ...this.tasks,
                            [task.pipelineStage]: [
                                task,
                                ...(this.tasks[task.pipelineStage] || []).sort((a, b) => a.order - b.order),
                            ],
                        };

                        this.incWorkflowPipelineTaskCount({
                            workflowId: data.workflowId,
                            pipelineId: task.pipelineStage,
                        });

                        this.publishTaskUpdate(data.workflowId);
                        break;

                    case SystemMessageTypes.TASK_UNARCHIVED:
                        const archivedTask = data.archivedTask;
                        this.tasks = {
                            ...this.tasks,
                            [archivedTask.pipelineStage]: [
                                archivedTask,
                                ...(this.tasks[archivedTask.pipelineStage] || []).sort((a, b) => a.order - b.order),
                            ],
                        };

                        this.incWorkflowPipelineTaskCount({
                            workflowId: data.workflowId,
                            pipelineId: archivedTask.pipelineStage,
                        });

                        this.publishTaskUpdate(data.workflowId);
                        break;
                    case SystemMessageTypes.TASK_DONE:
                        const taskDone = data.archivedTask || data.taskDone;
                        if (!taskDone) {
                            console.error("TASK_DONE olayında task verisi bulunamadı", data);
                            break;
                        }
                        this.tasks = {
                            ...this.tasks,
                            [taskDone.pipelineStage]: [
                                taskDone,
                                ...(this.tasks[taskDone.pipelineStage] || []).sort((a, b) => a.order - b.order),
                            ],
                        };

                        this.incWorkflowPipelineTaskCount({
                            workflowId: data.workflowId,
                            pipelineId: taskDone.pipelineStage,
                        });

                        this.publishTaskUpdate(data.workflowId);
                        break;
                }
            });

            const taskUpdateSocketStream = socketMessageStream.subscribe((message) => {
                if (message.cmd === "task_updated") {
                    const { userId, workflowId } = message.data;
                    if (userId !== this.user._id && workflowId === this.workflowId) {
                        this.updatedCount++;
                    }
                }
            });

            this.cancels.push(() => {
                taskActionSubscription.unsubscribe();
                taskUpdateSocketStream.unsubscribe();
            });
        },
        async initWorkflowTasks() {
            if (this.workflowId) {
                this.isLoading = true;
                try {
                    const params = {
                        workflowId: this.workflowId,
                        assignees: this.assignees,
                        showOverdue: this.showOverdue,
                        page: 1,
                        perPage: 20,
                        search: this.searchQuery,
                        priorities: this.selectedPriorities,
                        pipelines: this.selectedPipelines,
                        owner: this.owners,
                        followers: this.followers,
                        dateRange: this.dateRange,
                        createdBy: this.createdBy,
                        tags: this.selectedTags,
                        startDateRange: this.startDateRange,
                        endDateRange: this.endDateRange,
                        updateAtRange: this.updateAtRange,
                        sortBy: this.sortBy,
                    };

                    const tasks = await this.getWorkflowTasks(params);
                    this.tasks = tasks;
                } catch (error) {
                    console.error("Görevler yüklenirken hata:", error);
                    this.$notification.error({
                        message: this.$t("auto.tasksLoadErrorTitle"),
                        description: this.$t("auto.tasksLoadErrorDescription"),
                    });
                } finally {
                    this.isLoading = false;
                }
            }
        },
        handleResetFilters() {
            this.resetFilters();
        },
        resetFilters() {
            this.showOverdue = false;
            this.assignees = [];
            this.owners = [];
            this.followers = [];
            this.createdBy = [];
            this.selectedPriorities = [];
            this.selectedTags = [];
            this.selectedPipelines = [];
            this.searchQuery = "";
            this.dateRange = null;
            this.startDateRange = null;
            this.endDateRange = null;
            this.updateAtRange = null;
            this.sortBy = null;

            if (this.localType === "kanban") {
                this.initWorkflowTasks();
            } else if (this.localType === "list" && this.$refs.kanbanList) {
                this.$refs.kanbanList.getTasks();
            } else if (this.localType === "archive" && this.$refs.taskArchiveList) {
                this.$refs.taskArchiveList.getTasks();
            } else if (this.localType === "done" && this.$refs.doneTask) {
                this.$refs.doneTask.getTasks();
            }
        },
        handleApplyFilters(filters) {
            this.assignees = filters.assignees || [];
            this.owners = filters.owners || [];
            this.followers = filters.followers || [];
            this.createdBy = filters.createdBy || [];
            this.showOverdue = filters.showOverdue || false;
            this.selectedPriorities = filters.selectedPriorities || [];
            this.selectedTags = filters.selectedTags || [];
            this.selectedPipelines = filters.selectedPipelines || [];

            if (filters.dateRange && Array.isArray(filters.dateRange) && filters.dateRange.length === 2) {
                this.dateRange = filters.dateRange;
            } else {
                this.dateRange = null;
            }

            if (
                filters.startDateRange &&
                Array.isArray(filters.startDateRange) &&
                filters.startDateRange.length === 2
            ) {
                this.startDateRange = filters.startDateRange;
            } else {
                this.startDateRange = null;
            }

            if (filters.endDateRange && Array.isArray(filters.endDateRange) && filters.endDateRange.length === 2) {
                this.endDateRange = filters.endDateRange;
            } else {
                this.endDateRange = null;
            }

            if (filters.updateAtRange && Array.isArray(filters.updateAtRange) && filters.updateAtRange.length === 2) {
                this.updateAtRange = filters.updateAtRange;
            } else {
                this.updateAtRange = null;
            }

            if (filters.searchQuery !== undefined) {
                this.searchQuery = filters.searchQuery;
            }

            this.sortBy = filters.sortBy || null;

            if (this.localType === "kanban") {
                this.initWorkflowTasks();
            } else if (this.localType === "list" && this.$refs.kanbanList) {
                this.$refs.kanbanList.applyFilters(filters);
            } else if (this.localType === "archive" && this.$refs.taskArchiveList) {
                if (typeof this.$refs.taskArchiveList.applyFilters === "function") {
                    this.$refs.taskArchiveList.applyFilters(filters);
                } else {
                    if (typeof this.$refs.taskArchiveList.getTasks === "function") {
                        this.$refs.taskArchiveList.getTasks();
                    }
                }
            } else if (this.localType === "done" && this.$refs.doneTask) {
                if (typeof this.$refs.doneTask.applyFilters === "function") {
                    this.$refs.doneTask.applyFilters(filters);
                } else {
                    if (typeof this.$refs.doneTask.getTasks === "function") {
                        this.$refs.doneTask.getTasks();
                    }
                }
            }
        },
        handleWorkflowChange(value) {
            this.workflowId = value === "" || value === undefined ? null : value;

            console.log("Home.vue - Workflow değişti:", { newValue: value, workflowId: this.workflowId });

            setTimeout(() => {
                this.resetFilters();
            }, 500);

            if (this.localType === "kanban" && this.workflowId) {
                this.initWorkflowTasks();
            }
        },
        fnClicked(item) {
            if (item.key === "workflow") {
                this.$feature.beforeCreateTaskWorkflow(() => {
                    this.addNewWindow({
                        type: "create-workflow",
                    });
                });
            } else if (item.key === "addTask") {
                this.createTask();
            } else if (item.key === "workflowSettings") {
                this.openWorkflowSettings();
            } else if (item.key === "settings") {
                this.openTaskSettings();
            }
        },
        taskDetailPermission() {
            return [PERMISSION_TYPES["task.detailRead"].tr.name].some((permission) => this.checkPermission(permission));
        },
        openWorkflowSettings() {
            if (this.workflowId) {
                this.$router.push(`/tasks/workflow/${this.workflowId}/detail`);
            }
        },
        handleSearchQuery(query) {
            this.searchQuery = query;
            this.initWorkflowTasks();
        },
        handleFilterAssignees(assignees) {
            this.assignees = assignees;
            this.initWorkflowTasks();
        },
        handleFilterOther(filters) {
            console.log("Diğer filtreler alındı:", filters);

            if (filters.owners) this.owners = filters.owners;
            if (filters.followers) this.followers = filters.followers;
            if (filters.createdBy) this.createdBy = filters.createdBy;
            if (filters.showOverdue !== undefined) this.showOverdue = filters.showOverdue;
            if (filters.selectedPriorities) this.selectedPriorities = filters.selectedPriorities;
            if (filters.selectedTags) this.selectedTags = filters.selectedTags;
            if (filters.dateRange) this.dateRange = filters.dateRange;
            if (filters.startDateRange) this.startDateRange = filters.startDateRange;
            if (filters.endDateRange) this.endDateRange = filters.endDateRange;
            if (filters.updateAtRange) this.updateAtRange = filters.updateAtRange;

            this.initWorkflowTasks();
        },
        applyBatchOperation(taskIds) {
            this.changeCheckedTasks({ target: { checked: true } });
            this.checkedItems = taskIds.reduce((acc, taskId) => {
                acc[taskId] = true;
                return acc;
            }, {});
            this.checkedAllTask = this.checkedAllTasks();
        },
        openAddTask() {
            this.createTask();
        },
        handleCheckFilter(taskId) {
            this.checkedItems[taskId] = !this.checkedItems[taskId];
            this.checkedAllTask = this.checkedAllTasks();
        },
        toggleArchive(taskId) {},
        openCloseTasksModal() {
            this.closeTasksModal = true;
        },
        confirmCloseTasks() {
            const taskIds = this.batchOperationItems();
            const flattenTasks = Object.values(this.tasks).flat();
            const tasks = flattenTasks.filter((task) => taskIds.includes(task._id));
            const affectedPipelineIds = [];
            const seenPipeline = {};
            for (let i = 0; i < tasks.length; i++) {
                const stageId = tasks[i].pipelineStage;
                if (stageId && !seenPipeline[stageId]) {
                    seenPipeline[stageId] = true;
                    affectedPipelineIds.push(stageId);
                }
            }

            this.multipleTaskDone({ taskIds, tasks }).then(() => {
                this.publishTaskUpdate();
                const self = this;
                const pipelineIds = affectedPipelineIds.slice();
                return new Promise(function (resolve) {
                    self.$nextTick(function () {
                        const ref = self.$refs.taskDraggable;
                        if (ref && typeof ref.refillPipelineTasks === "function") {
                            ref.refillPipelineTasks(pipelineIds).then(resolve).catch(resolve);
                        } else {
                            resolve();
                        }
                    });
                });
            });

            this.$notification.success({
                message: this.$t("task.tasksBulkClosedTitle"),
                description: this.$t("task.tasksBulkClosedDescription", { count: taskIds.length }),
            });

            this.tasks = Object.keys(this.tasks).reduce((acc, key) => {
                acc[key] = this.tasks[key].filter((task) => !taskIds.includes(task._id));
                return acc;
            }, {});

            this.checkedItems = {};
            this.closeTasksModal = false;
        },
    },
    mounted() {
        this.getWorkFlows();
        this.initTaskActions();
    },
    beforeDestroy() {
        this.cancels.forEach((listener) => listener());
    },
    watch: {
        route: {
            deep: true,
            handler(newRoute) {
                const queryType = newRoute.query.type;
                if (queryType && queryType !== this.localType) {
                    this.localType = queryType;
                }

                if (!newRoute.query.workflow && !newRoute.query.type) {
                    this.localType = "workflowList";
                }

                if (newRoute.query.type !== this.$route.query.type) {
                    this.resetFilters();
                }
            },
        },
        "route.query.type": {
            immediate: true,
            handler(newType, oldType) {
                if (newType !== oldType) {
                    this.resetFilters();
                }
            },
        },
        showOverdue: {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (this.localType === "kanban" && this.workflowId) {
                        // this.initWorkflowTasks();
                    }
                }
            },
        },
        searchQuery: {
            handler(newVal) {
                if (this.$refs.taskFilterBar && this.$refs.taskFilterBar.searchQuery !== newVal) {
                    this.$refs.taskFilterBar.searchQuery = newVal;
                }

                if (this.localType === "kanban" && this.workflowId) {
                    this.initWorkflowTasks();
                } else if (this.localType === "list" && this.$refs.kanbanList) {
                    this.$refs.kanbanList.search = newVal;
                    this.$refs.kanbanList.getTasks();
                } else if (this.localType === "archive" && this.$refs.taskArchiveList) {
                    this.$refs.taskArchiveList.search = newVal;
                    this.$refs.taskArchiveList.getTasks();
                } else if (this.localType === "done" && this.$refs.doneTask) {
                    this.$refs.doneTask.search = newVal;
                    this.$refs.doneTask.getTasks();
                }
            },
        },
        selectedPriorities: {
            handler(newVal, oldVal) {
                if (!_.isEqual(newVal, oldVal)) {
                    if (this.localType === "kanban" && this.workflowId) {
                        this.initWorkflowTasks();
                    }
                }
            },
            deep: true,
        },
        checkedItems: {
            deep: true,
            handler() {
                this.checkedAllTask = this.checkedAllTasks();
            },
        },
        tasks: {
            deep: true,
            handler() {
                this.checkedAllTask = this.checkedAllTasks();
            },
        },
        assignees: {
            deep: true,
            handler(newVal, oldVal) {
                const isEqual = _.isEqual(newVal, oldVal);
                if (newVal && !isEqual) {
                    this.initWorkflowTasks();
                }
            },
        },
        owners: {
            deep: true,
            handler(newVal, oldVal) {
                const isEqual = _.isEqual(newVal, oldVal);
                if (newVal && !isEqual) {
                    this.initWorkflowTasks();
                }
            },
        },
        followers: {
            deep: true,
            handler(newVal, oldVal) {
                const isEqual = _.isEqual(newVal, oldVal);
                if (newVal && !isEqual) {
                    this.initWorkflowTasks();
                }
            },
        },
        createdBy: {
            handler(newVal, oldVal) {
                const isEqual = _.isEqual(newVal, oldVal);
                if (newVal && !isEqual) {
                    this.initWorkflowTasks();
                }
            },
            deep: true,
        },
        workflowId: {
            immediate: true,
            deep: true,
            handler(newVal, oldVal) {
                const safeNewVal = newVal || null;
                const safeOldVal = oldVal || null;

                if (safeNewVal !== safeOldVal) {
                    // Workflow değiştiğinde tasks'ları temizle
                    if (safeNewVal && safeOldVal && safeNewVal !== safeOldVal) {
                        this.tasks = {};
                        this.checkedItems = {};
                    }

                    if (this.localType === "kanban" && safeNewVal) {
                        // Her zaman API'den çek, state'ten okuma
                        this.initWorkflowTasks();
                    }
                }
            },
        },
        localType: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal && newVal !== oldVal) {
                    if (newVal !== "kanban" && newVal !== "reports") {
                        this.workflowId = null;
                    }
                    this.resetFilters();
                }
            },
        },
        taskId: {
            immediate: true,
            handler(newTaskId) {
                if (newTaskId && this.localType === "kanban") {
                    this.openTask(newTaskId);
                }
            },
        },
        dateRange: {
            handler(newVal, oldVal) {
                if (!_.isEqual(newVal, oldVal)) {
                    if (
                        newVal &&
                        newVal.length === 2 &&
                        typeof newVal[0] !== "string" &&
                        newVal[0] &&
                        newVal[0].format
                    ) {
                        this.dateRange = [newVal[0].format("YYYY-MM-DD"), newVal[1].format("YYYY-MM-DD")];
                    }

                    if (this.localType === "kanban" && this.workflowId) {
                        this.initWorkflowTasks();
                    }
                }
            },
            deep: true,
        },
    },
    directives: {
        "click-outside": {
            bind(el, binding) {
                el.clickOutsideEvent = function (event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.addEventListener("click", el.clickOutsideEvent);
            },
            unbind(el) {
                document.removeEventListener("click", el.clickOutsideEvent);
            },
        },
    },
};
</script>
<style lang="scss">
$color: #ec4b1d;

#tasks {
    .tasks-wrapper {
        display: flex;
        overflow: hidden;
        position: relative;
        height: calc(100vh - 93px);
    }

    .task-update-notification {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
        background-color: #fff;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        width: 350px;
        overflow: hidden;
        transition: all 0.3s ease;
        cursor: default;

        &:hover {
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
            transform: translateY(-2px);
        }

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 3px;
            background: linear-gradient(90deg, #1890ff, #52c41a);
        }

        .notification-close {
            position: absolute;
            top: 8px;
            right: 8px;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            border-radius: 50%;
            transition: background-color 0.3s;
            z-index: 2;

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
            }

            .material-icons {
                font-size: 18px;
                color: #999;
            }
        }

        .notification-content {
            padding: 16px;
            display: flex;
            align-items: flex-start;

            .notification-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 40px;
                height: 40px;
                background-color: rgba(24, 144, 255, 0.1);
                border-radius: 50%;
                margin-right: 12px;

                .material-icons {
                    color: #1890ff;
                    font-size: 22px;
                    animation: spin 2s linear infinite;
                }

                @keyframes spin {
                    0% {
                        transform: rotate(0deg);
                    }
                    100% {
                        transform: rotate(360deg);
                    }
                }
            }

            .notification-message {
                flex: 1;
                padding-right: 16px;

                .notification-title {
                    font-weight: 600;
                    font-size: 16px;
                    color: #333;
                    margin-bottom: 4px;
                }

                .notification-description {
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 12px;

                    .update-count {
                        font-weight: 600;
                        color: #1890ff;
                    }
                }

                .notification-actions {
                    display: flex;
                    gap: 8px;

                    .ant-btn {
                        display: flex;
                        align-items: center;
                        height: 32px;

                        .material-icons {
                            font-size: 16px;
                            margin-right: 4px;
                        }
                    }

                    .ant-btn-primary {
                        background-color: #1890ff;
                        border-color: #1890ff;

                        &:hover {
                            background-color: #40a9ff;
                            border-color: #40a9ff;
                        }
                    }
                }
            }
        }
    }

    // Fade animasyonu
    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.3s, transform 0.3s;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
        transform: translateY(-20px);
    }

    .classic-new-select {
        width: 200px;
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
        z-index: 1;
        color: white !important;
        background: #ec4b1d;
        border-color: #ec4b1d;
    }

    .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled)::before {
        background: #ec4b1d;
    }

    .ant-radio-button-wrapper {
        padding: 0px 40px;

        &:hover {
            color: #ec4b1d;
        }
    }

    .head-bar {
        background-color: var(--color-bg-neutral-default);
        border-bottom: 1px solid var(--color-border-default);
        min-height: 50px;
        display: flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: space-between;

        .title {
            line-height: 36px;
            color: #333338;
            font-size: 24px;
            font-weight: 400;
            letter-spacing: 0;
        }
    }

    .actions-button {
        display: flex;
        align-items: center;
        justify-content: center;

        .dropdown {
            padding: 10px;
        }
    }

    .more-also {
        &:hover {
            background-color: rgb(226 226 226 / 53%);
            padding: 3px;
            border-radius: 20px;
        }

        &.material-icons {
            padding: 10px;
        }
    }

    .board {
        width: 100%;
        height: calc(100% - 50px);
        padding: 0;
        margin: 0;

        .board-content {
            padding: 30px;
        }

        .board-todos {
            display: flex;
            height: 100%;

            .control-panel {
                background-color: white;
                width: 260px;
            }
        }
    }

    .radio-group-container {
        display: grid;
        grid-template-columns: 1fr;
    }

    .avatar-content {
        display: flex;
        align-items: center;
    }

    .avatar-content .avatar {
        width: 25px;
        height: 25px;
        border-radius: 50%;
        overflow: hidden;
        margin-right: 5px;
        display: flex;
        justify-content: center;
        align-items: center;
        background-color: #444;
        color: white;
        font-size: 12px;
        font-weight: bold;
    }

    .avatar-content .avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    .avatar-content .text-avatar {
        display: flex;
        justify-content: center;
        align-items: center;
        color: white;
        font-size: 12px;
        font-weight: bold;
    }

    .avatar-content .more {
        background-color: #555;
    }

    .avatar-container {
        position: relative;
        transition: transform 0.3s ease;
    }

    .avatar-container:hover {
        transform: scale(1.2);
        z-index: 10;
    }

    .custom-select {
        position: relative;
        width: 200px;
        border: 1px solid #dcdcdc;
        border-radius: 4px;
        background-color: #fff;
        cursor: pointer;
    }

    .options-workflow {
        position: absolute;
        top: 100%;
        left: 0;
        width: 100%;
        border: 1px solid #dcdcdc;
        border-top: none;
        background-color: #fff;
        z-index: 1000;
    }

    .option-workflow {
        padding: 10px;
        border-bottom: 1px solid #dcdcdc;
    }

    .option-workflow:hover {
        background-color: #f0f0f0;
    }

    .task-users-popover {
        .ant-popover-inner-content {
            max-height: 200px;
            overflow-y: auto;
            padding: 0;

            &::-webkit-scrollbar {
                width: 6px;
            }

            &::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 3px;
            }

            &::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 3px;

                &:hover {
                    background: #555;
                }
            }

            > div {
                padding: 12px;

                > div {
                    padding: 8px;
                    border-radius: 4px;
                    transition: background-color 0.2s ease;

                    &:hover {
                        background-color: rgba(0, 0, 0, 0.04);
                    }
                }
            }
        }
    }

    .workflow-select-container {
        margin-left: 15px;
        margin-right: 15px;
        display: flex;
        align-items: center;
    }

    .task-actions-container {
        display: flex;
        align-items: center;
    }

    .task-action-button {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;

        .action-icon {
            font-size: 20px;
            transition: transform 0.2s ease;
        }

        .action-text {
            font-weight: 500;
            font-size: 13px;
            white-space: nowrap;
        }

        &:hover {
            background-color: rgba(0, 0, 0, 0.05);

            .action-icon {
                transform: scale(1.1);
            }
        }

        .create-icon {
            color: #52c41a;
        }

        .archive-icon {
            color: #1890ff;
        }

        .done-icon {
            color: #722ed1;
        }
    }

    .action-divider {
        width: 1px;
        height: 24px;
        background-color: #e0e0e0;
        margin: 0 5px;
    }

    // Close Tasks Modal Styles
    .close-tasks-modal-container {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        max-width: 480px;
        width: 100%;
        overflow: hidden;
        animation: modalSlideIn 0.3s ease-out;
    }

    .close-tasks-modal-header {
        padding: 32px 28px 24px;
        text-align: center;
        background: linear-gradient(135deg, #f0fff4 0%, #ffffff 100%);

        .success-icon-wrapper {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 24px rgba(16, 185, 129, 0.3);
            position: relative;

            &::before {
                content: "";
                position: absolute;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                opacity: 0.3;
                animation: pulseSuccess 2s ease-in-out infinite;
            }

            @keyframes pulseSuccess {
                0%,
                100% {
                    transform: scale(1);
                    opacity: 0.3;
                }
                50% {
                    transform: scale(1.15);
                    opacity: 0.1;
                }
            }

            .success-icon {
                width: 40px;
                height: 40px;
                color: white;
                position: relative;
                z-index: 1;
            }
        }

        .close-tasks-modal-content {
            .close-tasks-modal-title {
                font-size: 22px;
                font-weight: 700;
                color: #1a1a1a;
                margin: 0 0 12px 0;
                line-height: 1.2;
            }

            .close-tasks-modal-message {
                font-size: 15px;
                color: #666;
                margin: 0 0 16px 0;
                line-height: 1.6;
            }

            .close-tasks-modal-info {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background-color: rgba(16, 185, 129, 0.08);
                border-radius: 20px;
                border: 1px solid rgba(16, 185, 129, 0.2);

                .info-icon {
                    width: 18px;
                    height: 18px;
                    color: #10b981;
                    flex-shrink: 0;
                }

                span {
                    font-size: 13px;
                    font-weight: 600;
                    color: #10b981;
                }
            }
        }
    }

    .close-tasks-modal-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 20px 28px 28px;
        background-color: white;

        .modal-btn {
            flex: 1;
            padding: 12px 24px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            outline: none;
            max-width: 180px;

            &:active {
                transform: scale(0.98);
            }

            .btn-icon {
                width: 18px;
                height: 18px;
            }

            &.modal-btn-cancel {
                background-color: #f5f5f5;
                color: #666;
                border: 1px solid #e0e0e0;

                &:hover {
                    background-color: #ebebeb;
                    border-color: #d0d0d0;
                    color: #333;
                }
            }

            &.modal-btn-success {
                background: linear-gradient(135deg, #10b981 0%, #059669 100%);
                color: white;
                box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);

                &:hover {
                    box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0) scale(0.98);
                }
            }
        }
    }

    // Custom Modal Styles
    .modal-fade-enter-active,
    .modal-fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .modal-fade-enter,
    .modal-fade-leave-to {
        opacity: 0;
    }

    .modal-fade-enter-active .custom-modal-container,
    .modal-fade-leave-active .custom-modal-container {
        transition: transform 0.3s ease, opacity 0.3s ease;
    }

    .modal-fade-enter .custom-modal-container,
    .modal-fade-leave-to .custom-modal-container {
        transform: scale(0.9) translateY(-20px);
        opacity: 0;
    }

    .custom-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        padding: 20px;
    }

    .custom-modal-container {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        max-width: 700px;
        width: 100%;
        max-height: 85vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        animation: modalSlideIn 0.3s ease-out;
    }

    @keyframes modalSlideIn {
        from {
            transform: scale(0.95) translateY(-20px);
            opacity: 0;
        }
        to {
            transform: scale(1) translateY(0);
            opacity: 1;
        }
    }

    .custom-modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 24px 28px;
        border-bottom: 1px solid #f0f0f0;
        background: linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%);

        .modal-header-content {
            display: flex;
            align-items: center;
            gap: 16px;
            flex: 1;

            .modal-icon-wrapper {
                width: 48px;
                height: 48px;
                border-radius: 12px;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

                .modal-icon {
                    width: 24px;
                    height: 24px;
                    color: white;
                }
            }

            .modal-title-section {
                .modal-title {
                    font-size: 20px;
                    font-weight: 600;
                    color: #1a1a1a;
                    margin: 0 0 4px 0;
                    line-height: 1.2;
                }

                .modal-subtitle {
                    font-size: 13px;
                    color: #666;
                    margin: 0;
                    line-height: 1.2;
                }
            }
        }

        .modal-close-btn {
            width: 32px;
            height: 32px;
            border: none;
            background: transparent;
            border-radius: 8px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s ease;
            color: #666;

            svg {
                width: 20px;
                height: 20px;
            }

            &:hover {
                background-color: rgba(0, 0, 0, 0.05);
                color: #333;
            }

            &:active {
                transform: scale(0.95);
            }
        }
    }

    .custom-modal-body {
        padding: 28px;
        overflow-y: auto;
        flex: 1;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 3px;

            &:hover {
                background: #999;
            }
        }

        .pipeline-selection-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 16px;
        }
    }

    .pipeline-card {
        background: white;
        border: 2px solid #e8e8e8;
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.3s ease;
        overflow: hidden;
        position: relative;

        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
            border-color: #d0d0d0;
        }

        &.selected {
            border-color: #47499d;
            background: linear-gradient(135deg, #f5f6ff 0%, #ffffff 100%);
            box-shadow: 0 8px 24px rgba(71, 73, 157, 0.15);

            .pipeline-color-indicator {
                transform: scaleY(1.1);
            }

            .pipeline-check {
                opacity: 1;
                transform: scale(1);
            }
        }

        .pipeline-card-inner {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 16px;
            position: relative;
        }

        .pipeline-color-indicator {
            width: 6px;
            height: 56px;
            border-radius: 3px;
            flex-shrink: 0;
            transition: transform 0.3s ease;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .pipeline-content {
            flex: 1;
            min-width: 0;

            .pipeline-name {
                font-size: 15px;
                font-weight: 600;
                color: #1a1a1a;
                margin-bottom: 6px;
                line-height: 1.3;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .pipeline-details {
                display: flex;
                gap: 12px;
                font-size: 12px;
                color: #666;

                .pipeline-order {
                    display: flex;
                    align-items: center;
                }

                .pipeline-count {
                    display: flex;
                    align-items: center;
                    padding: 2px 8px;
                    background-color: rgba(0, 0, 0, 0.05);
                    border-radius: 10px;
                    font-weight: 500;
                }
            }
        }

        .pipeline-check {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transform: scale(0.8);
            transition: all 0.3s ease;
            flex-shrink: 0;

            svg {
                width: 18px;
                height: 18px;
                color: white;
            }
        }
    }

    .custom-modal-footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 20px 28px;
        border-top: 1px solid #f0f0f0;
        background-color: #fafafa;

        .modal-btn {
            padding: 10px 24px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 8px;
            outline: none;

            &:active {
                transform: scale(0.98);
            }

            .btn-icon {
                width: 16px;
                height: 16px;
            }

            &.modal-btn-cancel {
                background-color: white;
                color: #666;
                border: 1px solid #e0e0e0;

                &:hover {
                    background-color: #f5f5f5;
                    border-color: #d0d0d0;
                    color: #333;
                }
            }

            &.modal-btn-primary {
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                color: white;
                box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

                &:hover {
                    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0) scale(0.98);
                }

                &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                    box-shadow: none;

                    &:hover {
                        transform: none;
                    }
                }
            }
        }
    }

    // Delete Modal Styles
    .delete-modal-container {
        background: white;
        border-radius: 16px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
        max-width: 480px;
        width: 100%;
        overflow: hidden;
        animation: modalSlideIn 0.3s ease-out;
    }

    .delete-modal-header {
        padding: 32px 28px 24px;
        text-align: center;
        background: linear-gradient(135deg, #fff5f5 0%, #ffffff 100%);

        .warning-icon-wrapper {
            width: 80px;
            height: 80px;
            margin: 0 auto 20px;
            border-radius: 50%;
            background: linear-gradient(135deg, #ff4757 0%, #ff6348 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 8px 24px rgba(255, 71, 87, 0.3);
            position: relative;

            &::before {
                content: "";
                position: absolute;
                width: 100%;
                height: 100%;
                border-radius: 50%;
                background: linear-gradient(135deg, #ff4757 0%, #ff6348 100%);
                opacity: 0.3;
                animation: pulse 2s ease-in-out infinite;
            }

            @keyframes pulse {
                0%,
                100% {
                    transform: scale(1);
                    opacity: 0.3;
                }
                50% {
                    transform: scale(1.15);
                    opacity: 0.1;
                }
            }

            .warning-icon {
                width: 40px;
                height: 40px;
                color: white;
                position: relative;
                z-index: 1;
            }
        }

        .delete-modal-content {
            .delete-modal-title {
                font-size: 22px;
                font-weight: 700;
                color: #1a1a1a;
                margin: 0 0 12px 0;
                line-height: 1.2;
            }

            .delete-modal-message {
                font-size: 15px;
                color: #666;
                margin: 0 0 16px 0;
                line-height: 1.6;
            }

            .delete-modal-info {
                display: inline-flex;
                align-items: center;
                gap: 8px;
                padding: 8px 16px;
                background-color: rgba(255, 71, 87, 0.08);
                border-radius: 20px;
                border: 1px solid rgba(255, 71, 87, 0.2);

                .info-icon {
                    width: 18px;
                    height: 18px;
                    color: #ff4757;
                    flex-shrink: 0;
                }

                span {
                    font-size: 13px;
                    font-weight: 600;
                    color: #ff4757;
                }
            }
        }
    }

    .delete-modal-footer {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 12px;
        padding: 20px 28px 28px;
        background-color: white;

        .modal-btn {
            flex: 1;
            padding: 12px 24px;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 600;
            cursor: pointer;
            border: none;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            outline: none;
            max-width: 180px;

            &:active {
                transform: scale(0.98);
            }

            .btn-icon {
                width: 18px;
                height: 18px;
            }

            &.modal-btn-cancel {
                background-color: #f5f5f5;
                color: #666;
                border: 1px solid #e0e0e0;

                &:hover {
                    background-color: #ebebeb;
                    border-color: #d0d0d0;
                    color: #333;
                }
            }

            &.modal-btn-danger {
                background: linear-gradient(135deg, #ff4757 0%, #ff6348 100%);
                color: white;
                box-shadow: 0 4px 12px rgba(255, 71, 87, 0.3);

                &:hover {
                    box-shadow: 0 6px 16px rgba(255, 71, 87, 0.4);
                    transform: translateY(-1px);
                }

                &:active {
                    transform: translateY(0) scale(0.98);
                }
            }
        }
    }
}
</style>
